import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Card,
  Empty,
  Flex,
  Form,
  FormListFieldData,
  InputNumber,
  Select,
  Spin,
  Table,
  TableColumnsType
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useMemo, useState } from 'react'

import { useStocksServiceGetInventoryListEndpointKey } from 'src/api/queries'
import {
  InquiryDocumentResponse,
  InventoryListResponse,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { EditableFormItem, SubmitButtons, Text } from 'src/components'
import { useStyles } from 'src/features/stock/components/applications/styles'
import { useDebounce, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  fields: FormListFieldData[]
  form?: FormInstance
  isFinished?: boolean
  remove: (index: number) => void
}

const fieldsToReset = [
  'barCode',
  'name',
  'inventoryItemId',
  'expirationDate',
  'producer',
  'form',
  'quantity'
]

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      fieldsToReset.map((field) => ['medicine', index, field])
    )
    .flat()

export const LocalConsumableListCreate = ({
  fields,
  form,
  remove,
  isFinished
}: Props) => {
  const [search, setSearch] = useState('')

  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)

  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const { styles } = useStyles()
  const palette = useTheme()

  const { userData } = useUser()
  const {
    data,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetInventoryListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInventoryListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        [String(userData?.currentLegalEntityId)]
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: InventoryListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap((page) => page.data) || []

  const selectOptions = options?.map((item) => ({
    label: `${item?.internationalName?.name} ${item?.doses}`,
    value: item?.id,
    item
  }))

  const list = Form.useWatch('medicine', form) || []

  const filteredCombinedData = useMemo(() => {
    const selectedIds = new Set(
      list.map((item: InquiryDocumentResponse) => item?.id)
    )

    return selectOptions.filter((data) => !selectedIds.has(data.value))
  }, [selectOptions, list])

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(
        fieldsToReset.map((field) => ['medicine', index, field])
      )
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onSelect = async (value: string) => {
    const medicines = form?.getFieldValue('medicine')

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    const selectedItem = selectOptions?.find((item) => item.value === value)

    if (selectedItem) {
      const newInquiryItem = {
        barCode: selectedItem?.item?.barCode,
        name: selectedItem.label,
        id: selectedItem.value,
        inventoryItemId: selectedItem.value,
        producer: selectedItem?.item?.producer?.name,
        form: {
          id: selectedItem?.item?.productForm?.id,
          name: selectedItem?.item?.productForm?.name
        },
        item: selectedItem.item
      }

      form?.setFieldsValue({ medicine: [...list, newInquiryItem] })
      setEditingIndex(list.length)
      setIsNewConsumable(true)
      setSearch('')
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue('medicine')

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({ medicine: values?.medicine })
    }
  }

  const columns: TableColumnsType = useMemo(
    () => [
      {
        key: 'barCode',
        title: t('stock_applications.series'),
        render: (_, __, index) => {
          const code = form?.getFieldValue(['medicine', index, 'barCode'])

          return code ?? '-'
        }
      },
      {
        key: 'product',
        title: t('common.commercial_name'),
        render: (_, __, index) => {
          const name = form?.getFieldValue(['medicine', index, 'name'])

          return <Text color={palette['blue-6']}>{name}</Text>
        }
      },
      {
        key: 'producer',
        title: t('common.producer'),
        render: (_, __, index) => {
          const producer = form?.getFieldValue(['medicine', index, 'producer'])

          return <Text color={palette.cyan7}>{producer}</Text>
        }
      },
      {
        key: 'quantity',
        minWidth: 200,
        title: t('stock_write_offs.write_off_quantity'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'quantity']}
              isEditing={editingIndex === index}
              rules={requiredRules}
            >
              <InputNumber
                min={1}
                placeholder={t('common.quantity')}
                className="full-width"
              />
            </EditableFormItem>
          )
        }
      },
      {
        key: 'um',
        title: t('common.unit'),
        render: (_, __, index) => {
          const um = form?.getFieldValue(['medicine', index, 'form', 'name'])

          return <Text>{um}</Text>
        }
      },
      {
        width: 105,
        fixed: 'right' as const,
        key: 'actions',
        render: (_: unknown, __: unknown, index: number) => (
          <SubmitButtons
            editingIndex={editingIndex}
            isEditing={editingIndex === index}
            onSave={onSave}
            onCancel={() => onCancel(index)}
            onEdit={() => setEditingIndex(index)}
            onDelete={() => remove(index)}
          />
        )
      }
    ],
    [t, palette]
  )

  return (
    <Card
      title={t('stock_applications.requested_medicine')}
      className={styles.applicationTable}
    >
      <Table
        dataSource={fields}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        columns={columns}
        pagination={false}
      />

      {!isFinished && (
        <Card bordered={false} className={styles.applicationTableItem}>
          <Select
            allowClear
            showSearch
            value={null}
            onSearch={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            loading={isFetchingMedicines}
            options={filteredCombinedData}
            filterOption={false}
            onSelect={(value) => value && onSelect(value)}
            placeholder={t('common.search')}
            onPopupScroll={(e) => {
              const target = e.target as HTMLElement
              if (
                target.scrollTop + target.offsetHeight ===
                target.scrollHeight
              ) {
                fetchNextMedicinesPage()
              }
            }}
            notFoundContent={
              isFetchingMedicines ? (
                <Flex justify="center">
                  <Spin spinning />
                </Flex>
              ) : (
                <Empty />
              )
            }
          />
        </Card>
      )}
    </Card>
  )
}
